"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Laser Engraving — Phenolic",
  slug: "/services/laser-engraving-phenolic/",
  descriptor: "Two-color engraved legend plates, nameplates, and labels in true phenolic and two-color acrylic. Stock colors, thicknesses, and adhesive or fastener mounting — the standard for control-panel identification.",
  linkText: "Quote phenolic engraving",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/Phenolic_Legend_Plates-scaled-e1781520243456.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/Phenolic_Legend_Plates-scaled-e1781520243456.png",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/Phenolic_Legend_Plates-scaled-e1781520243456.png",
  alt: "Phenolic engraving product photo showing two-color legend plates"
}, {
  title: "Laser Engraving — Metal",
  slug: "/services/laser-engraving-metal/",
  descriptor: "Stainless steel, anodized aluminum, and brass engraving with a range of finishes and fill colors. Built for harsh environments, outdoor installs, and high-temperature applications.",
  linkText: "Quote metal engraving",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/metal_engraving_industrial.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/metal_engraving_industrial.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/metal_engraving_industrial.png-wvc-sizes",
  alt: "Metal engraving product photo featuring stainless steel and anodized aluminum tags"
}, {
  title: "Direct Part Marking (DPM)",
  slug: "/services/laser-marking-direct-part/",
  descriptor: "Permanent direct part marking on metal — 2D data matrix, serialization, and traceability for OEM and aerospace components that must stay readable for the life of the part.",
  linkText: "Quote direct part marking",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/direct_part_marking_dpm.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/direct_part_marking_dpm.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/direct_part_marking_dpm.png-wvc-sizes",
  alt: "Direct part marking photo showing 2D data matrix on aerospace components"
}, {
  title: "Arc Flash Labels",
  slug: "/services/arc-flash-labels/",
  descriptor: "Arc flash labels carrying incident-energy, arc-rating, system-voltage, and analysis-date fields. Produced to align with NFPA 70E and NEC 110.16 when specified from your study.",
  linkText: "Quote arc flash labels",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels_safety.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels_safety.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels_safety.png-wvc-sizes",
  alt: "Arc flash labels photo showing compliant safety identification stickers"
}, {
  title: "Acrylic Signs and Cutting",
  slug: "/services/acrylic-signs-cutting/",
  descriptor: "Laser-cut and engraved acrylic signs, nameplates, and custom cut parts for industrial and facility applications. Cast acrylic in clear, frosted, colored, and two-color laminate.",
  linkText: "Quote acrylic cutting",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_signs_cutting.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_signs_cutting.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/acrylic_signs_cutting.png-wvc-sizes",
  alt: "Acrylic signs and laser cutting product photo"
}, {
  title: "Custom Engraving",
  slug: "/services/custom-engraving/",
  descriptor: "Non-standard project? Send the file and requirement. Evermark scopes material, mounting, and tolerance, then quotes one piece or a full run.",
  linkText: "Start a custom quote",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_engraving_project.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_engraving_project.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/custom_engraving_project.png-wvc-sizes",
  alt: "Custom engraving photo showcasing a unique industrial marking project"
}, {
  title: "Acryllic Signs and Cutting",
  slug: "Acryllic Signs and Cutting",
  descriptor: "Laser customize sign fabrication offers elegance and beauty unique to your design.",
  linkText: "Contact us",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/submittal_packages_documentation.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/submittal_packages_documentation.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/submittal_packages_documentation.png-wvc-sizes",
  alt: "Submittal packages photo showing technical documentation and material data sheets"
}];
export default function ServiceCardGrid() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="service-card-grid" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`max-w-3xl mb-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl font-bold font-default mb-4 text-foreground">
            What Industrial Engraving Services Does Evermark Provide?
          </h2>
          <p className="text-lg text-muted-foreground">
            Pick the service that matches the work, or send a file on the quote
            page and Evermark routes it to the right process.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => <div data-index={i} key={i} className={`transition-all duration-700 ease-out delay-${i * 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Card className="h-full bg-secondary border-2 border-transparent hover:border-primary transition-colors duration-300 flex flex-col shadow-md overflow-hidden">
                <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} className="w-full h-48 object-cover" loading="lazy" decoding="async" />
                <CardHeader>
                  <CardTitle className="text-xl font-default text-foreground">
                    <Link className="hover:text-primary transition-colors" to={service.slug}>
                      {service.title}
                    </Link>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-secondary-foreground/90 text-sm leading-relaxed">
                    {service.descriptor}
                  </p>
                </CardContent>
                <CardFooter>
                  <Link to={service.slug} className="text-primary font-medium inline-flex items-center gap-2 hover:underline group text-sm">
                    {service.linkText}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}